import {useInfiniteQuery, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useIntl} from "react-intl";
import {message} from "antd";
import {queryKeys} from "utils";
import {createAsset, createAssetLike, deleteAsset, deleteAssetLike, getAsset, getAssetLike, getAssetList, updateAsset} from "api-v3";
import {ASSET_REQUEST_LIMIT} from "utils/constants/assets";
import {ASSET_PARAMS} from "components/Layout/constants";

export const useGetAssetQuery = (id, options) => {
  return useQuery(
    queryKeys.asset(id),
    async () => {
      const queryParams = {id};
      const response = await getAsset(queryParams);
      return response.data;
    },
    {
      staleTime: 3600 * 1000,
      cacheTime: 3600 * 1000,
      ...options,
    },
  );
};

export const useGetAssetsQuery = (assetType, params, limit = 10) => {
  return useQuery(
    queryKeys.asset(assetType),
    async () => {
      const queryParams = {...params, limit};
      const response = await getAssetList(queryParams);
      response.data.items.totalCount = response.data.totalCount;
      return response.data.items;
    },
    {
      staleTime: 3600 * 1000,
      cacheTime: 3600 * 1000,
      suspense: true,
    },
  );
};

export const useGetAssetsInfiniteQuery = params => {
  return useInfiniteQuery(
    queryKeys.asset(params),
    async ({pageParam = 0}) => {
      const parameter = {...params};
      parameter.limit = ASSET_REQUEST_LIMIT;
      parameter.offset = ASSET_REQUEST_LIMIT * pageParam;

      const response = await getAssetList(parameter);
      response.data.items.totalCount = response.data.totalCount;
      return await response.data.items;
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        return lastPage?.length === ASSET_REQUEST_LIMIT && allPages.length;
      },
      staleTime: 3600 * 1000,
      cacheTime: 3600 * 1000,
      suspense: true,
    },
  );
};

export const useCreateAssetsQuery = () => {
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation(
    async params => {
      try {
        await createAsset(params);
      } catch (e) {
        message.error(intl.formatMessage({id: "ID_ERROR_SAVE_ASSET"}));
      }
    },
    {
      onSuccess: () => queryClient.invalidateQueries(queryKeys.allAssetKey),
    },
  );
};

export const useUpdateAssetsQuery = id => {
  const queryClient = useQueryClient();
  return useMutation(
    async params => {
      await updateAsset({id}, params);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.allAssetKey);
      },
    },
  );
};

export const useDeleteAssetsQuery = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async idList => {
      let idString = "";
      if (Array.isArray(idList)) {
        idString = idList.join(",");
      } else {
        idString = idList;
      }
      await deleteAsset({id: idString});
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.allAssetKey);
      },
    },
  );
};

export const useGetFavAssetsQuery = assetType => {
  return useInfiniteQuery(
    queryKeys.favAssets(assetType),
    async ({pageParam = 0}) => {
      const params = {
        ...ASSET_PARAMS[assetType],
        limit: ASSET_REQUEST_LIMIT,
        offset: ASSET_REQUEST_LIMIT * pageParam,
      };
      const response = await getAssetLike(params);
      response.data.items.totalCount = response.data.totalCount;
      return response.data.items;
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        return lastPage?.length === ASSET_REQUEST_LIMIT && allPages.length;
      },
      staleTime: 3600 * 1000,
      cacheTime: 3600 * 1000,
    },
  );
};

export const useFavAssetsQuery = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({id, likeStatus}) => {
      if (!likeStatus) {
        await createAssetLike({id});
      } else {
        await deleteAssetLike({id});
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.favAssets);
        queryClient.invalidateQueries(queryKeys.allAssetKey);
      },
    },
  );
};
// TODO: 기존 쿼리 사용 컴포넌트 삭제 후 삭제
export const useRecAssetsQuery = (assetType, limit = 10) => {
  return useQuery(
    queryKeys.recAsset(assetType),
    async () => {
      const params = {assetType, limit, recommended: true, publicStatus: true};
      const response = await getAssetList(params);
      response.data.items.totalCount = response.data.totalCount;
      return response.data.items;
    },
    {
      staleTime: 3600 * 1000,
      cacheTime: 3600 * 1000,
    },
  );
};
